import { useState, useEffect } from 'react';
import AdminLayout from '@/components/admin/AdminLayout';
import axios from 'axios';
import { 
  Server, Cpu, Database, Globe, Activity, RefreshCcw, 
  Terminal, ShieldCheck, AlertTriangle, Info, HardDrive, Clock
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminSystem() {
  const [status, setStatus] = useState(null);
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [creatingBackup, setCreatingBackup] = useState(false);

  const fetchStatus = async () => {
    setRefreshing(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/system/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStatus(res.data);
    } catch (err) {
      console.error('Error fetching system status:', err);
      toast.error('Error al obtener el estado del sistema');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const fetchBackups = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/admin/backups`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBackups(res.data.backups || []);
    } catch (err) {
      console.error('Error fetching backups:', err);
    }
  };

  const handleCreateBackup = async () => {
    setCreatingBackup(true);
    const toastId = toast.loading('Generando backup de la base de datos...');
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/admin/backup/create`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.status === 'ok') {
        toast.success(res.data.message, { id: toastId });
        fetchBackups();
      } else {
        toast.error(res.data.message, { id: toastId });
      }
    } catch (err) {
      toast.error('Error al crear backup', { id: toastId });
    } finally {
      setCreatingBackup(false);
    }
  };

  useEffect(() => {
    fetchStatus();
    fetchBackups();
  }, []);

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <Activity className="w-8 h-8 text-primary animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  const ProcessCard = ({ name, active, icon: Icon }) => (
    <div className={`p-4 rounded-xl border ${active ? 'bg-emerald-50 border-emerald-100' : 'bg-red-50 border-red-100'} flex items-center justify-between`}>
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${active ? 'bg-emerald-500 text-white' : 'bg-red-500 text-white'}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <p className="font-bold text-slate-700 capitalize">{name}</p>
          <p className={`text-xs ${active ? 'text-emerald-600' : 'text-red-600'}`}>
            {active ? '● Ejecutándose' : '○ Detenido'}
          </p>
        </div>
      </div>
    </div>
  );

  return (
    <AdminLayout>
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-serif font-semibold text-primary">Estado del Sistema</h1>
          <p className="text-muted-foreground">Monitorización de procesos y salud del servidor</p>
        </div>
        <Button 
          onClick={fetchStatus} 
          disabled={refreshing}
          variant="outline"
          className="gap-2"
        >
          <RefreshCcw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          Refrescar
        </Button>
      </div>

      <div className="grid lg:grid-cols-3 gap-8 mb-8">
        {/* Hardware Status */}
        <div className="lg:col-span-2 grid sm:grid-cols-2 gap-4">
          <div className="bg-white p-6 rounded-xl border border-slate-100 shadow-sm flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-blue-50 flex items-center justify-center text-blue-600">
              <Cpu className="w-6 h-6" />
            </div>
            <div>
              <p className="text-sm text-slate-400 font-medium uppercase tracking-wider">CPU Usage</p>
              <p className="text-2xl font-bold text-slate-700">{status?.hardware?.cpu}</p>
            </div>
          </div>
          <div className="bg-white p-6 rounded-xl border border-slate-100 shadow-sm flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-purple-50 flex items-center justify-center text-purple-600">
              <Activity className="w-6 h-6" />
            </div>
            <div>
              <p className="text-sm text-slate-400 font-medium uppercase tracking-wider">Memoria RAM</p>
              <p className="text-xl font-bold text-slate-700">{status?.hardware?.memory}</p>
            </div>
          </div>
          <div className="bg-white p-6 rounded-xl border border-slate-100 shadow-sm flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-orange-50 flex items-center justify-center text-orange-600">
              <HardDrive className="w-6 h-6" />
            </div>
            <div>
              <p className="text-sm text-slate-400 font-medium uppercase tracking-wider">Disco Duro (FS)</p>
              <p className="text-xl font-bold text-slate-700">{status?.hardware?.disk}</p>
            </div>
          </div>
          <div className="bg-white p-6 rounded-xl border border-slate-100 shadow-sm flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-slate-50 flex items-center justify-center text-slate-600">
              <Clock className="w-6 h-6" />
            </div>
            <div>
              <p className="text-sm text-slate-400 font-medium uppercase tracking-wider">S.O. Uptime</p>
              <p className="text-xl font-bold text-slate-700">{status?.uptime}</p>
            </div>
          </div>
        </div>

        {/* System Info */}
        <div className="bg-primary text-white p-6 rounded-xl shadow-lg flex flex-col justify-between">
           <div>
              <div className="flex items-center gap-2 mb-4">
                <ShieldCheck className="w-5 h-5 text-emerald-400" />
                <h3 className="font-bold text-lg">Información de Seguridad</h3>
              </div>
              <p className="text-sm text-blue-100 mb-4 italic">
                Carga de trabajo óptima para el servidor detectada.
              </p>
              <div className="space-y-2 text-xs">
                <div className="flex justify-between border-b border-white/10 pb-2">
                  <span className="opacity-60">SO:</span>
                  <span>{status?.hardware?.os}</span>
                </div>
                <div className="flex justify-between border-b border-white/10 pb-2">
                  <span className="opacity-60">Entorno:</span>
                  <span className="font-bold">PRODUCCIÓN</span>
                </div>
                <div className="flex justify-between border-b border-white/10 pb-2">
                  <span className="opacity-60">Último Reinicio S.O:</span>
                  <span>{status?.uptime} atrás</span>
                </div>
              </div>
           </div>
           <Button 
            variant="secondary" 
            className="mt-6 w-full text-primary font-bold gap-2"
            onClick={handleCreateBackup}
            disabled={creatingBackup}
           >
              {creatingBackup ? <RefreshCcw className="w-4 h-4 animate-spin" /> : <Database className="w-4 h-4" />}
              Backup Base de Datos
           </Button>
        </div>
      </div>

      {/* Backups List */}
      <div className="bg-white p-6 rounded-xl border border-slate-100 shadow-sm mb-8">
        <h3 className="text-lg font-serif font-bold text-primary mb-4 flex items-center gap-2">
          <Database className="w-5 h-5" /> Historial de Backups
        </h3>
        <p className="text-sm text-slate-500 mb-6">
          Los backups se guardan en el servidor en la carpeta <code>/backups/</code>.
        </p>
        
        {backups.length === 0 ? (
          <div className="text-center py-8 border-2 border-dashed border-slate-100 rounded-xl">
            <p className="text-slate-400">No hay backups disponibles.</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {backups.map((backup) => (
              <div key={backup.id} className="p-4 rounded-lg bg-slate-50 border border-slate-100">
                <div className="flex justify-between items-start mb-2">
                  <span className="text-xs font-bold text-slate-400 uppercase tracking-tighter">
                    {new Date(backup.date).toLocaleDateString()} {new Date(backup.date).toLocaleTimeString()}
                  </span>
                  <Database className="w-4 h-4 text-slate-300" />
                </div>
                <p className="font-mono text-[10px] text-slate-600 truncate mb-2">{backup.id}</p>
                <div className="flex justify-between items-center">
                  <span className="text-xs text-slate-500">{backup.files_count} colecciones</span>
                  <span className="text-[10px] bg-slate-200 text-slate-600 px-2 py-0.5 rounded-full font-bold">V2.0 Core</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        {/* Processes Tracking */}
        <div className="bg-white p-6 rounded-xl border border-slate-100 shadow-sm">
          <h3 className="text-lg font-serif font-bold text-primary mb-6 flex items-center gap-2">
            <Terminal className="w-5 h-5" /> Seguimiento de Procesos
          </h3>
          <div className="grid sm:grid-cols-2 gap-4">
            <ProcessCard name="Backend (API)" active={status?.processes?.backend} icon={Activity} />
            <ProcessCard name="MongoDB" active={status?.processes?.mongodb} icon={Database} />
            <ProcessCard name="Nginx (Web)" active={status?.processes?.nginx} icon={Globe} />
            <ProcessCard name="Frontend" active={status?.processes?.frontend} icon={Globe} />
          </div>
        </div>

        {/* Documentation & Recommendations */}
        <div className="space-y-6">
          <div className="bg-white p-6 rounded-xl border border-slate-100 shadow-sm">
            <h3 className="text-lg font-serif font-bold text-primary mb-4 flex items-center gap-2">
              <Info className="w-5 h-5" /> Documentación de Reinicio
            </h3>
            <div className="space-y-4">
              {Object.entries(status?.documentation || {}).map(([key, cmd]) => (
                <div key={key} className="group">
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-tighter mb-1">
                    {key.replace('_', ' ')}
                  </p>
                  <div className="bg-slate-900 text-slate-300 p-2 rounded font-mono text-xs flex justify-between items-center">
                    <code>{cmd}</code>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-amber-50 p-6 rounded-xl border border-amber-100">
            <h3 className="text-lg font-serif font-bold text-amber-800 mb-4 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" /> Recomendaciones Performance
            </h3>
            <ul className="space-y-2">
              {status?.recommendations?.map((rec, idx) => (
                <li key={idx} className="flex gap-2 text-sm text-amber-700">
                  <span className="font-bold">•</span>
                  {rec}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
